import { fmtMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, X, Award, Coins } from 'lucide-react';
import {
  useToast, useConfirm,
  SkeletonRow, EmptyState,
  PageHeader, StatusBadge, Button, Input,
} from '../components/ui';

const API = process.env.REACT_APP_API_URL || '/api';

const tierVariant = (tier) => {
  switch (tier) {
    case 'platinum': return 'primary';
    case 'gold':     return 'warning';
    case 'silver':   return 'neutral';
    case 'bronze':   return 'warning';
    default:         return 'neutral';
  }
};

const tierAccent = (tier) => {
  switch (tier) {
    case 'platinum': return 'border-primary-200 bg-primary-50';
    case 'gold':     return 'border-warning-300 bg-warning-50';
    case 'silver':   return 'border-neutral-300 bg-neutral-50';
    case 'bronze':   return 'border-warning-200 bg-warning-50/50';
    default:         return 'border-neutral-200 bg-neutral-50';
  }
};

const Customers = () => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState('');
  const [loyaltyAction, setLoyaltyAction] = useState('add');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    dateOfBirth: '',
    preferences: [],
    allergies: [],
    notes: ''
  });

  const [tierInfo, setTierInfo] = useState([]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/customers?search=${searchTerm}&tier=${filterTier}`, {
        headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      if (res.ok) setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTierInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/customers/loyalty-tiers`, {
        headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      if (res.ok) setTierInfo(data);
    } catch (err) {
      console.error('Error fetching tier info:', err);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchTierInfo();
  }, [searchTerm, filterTier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingCustomer
        ? `${API}/customers/${editingCustomer._id}`
        : `${API}/customers`;
      const method = editingCustomer ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingCustomer(null);
        resetForm();
        fetchCustomers();
        toast.success(editingCustomer ? 'Customer updated' : 'Customer added');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.msg || 'Could not save customer');
      }
    } catch (err) {
      toast.error('Could not save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '',
      dateOfBirth: customer.dateOfBirth ? customer.dateOfBirth.split('T')[0] : '',
      preferences: customer.preferences || [],
      allergies: customer.allergies || [],
      notes: customer.notes || ''
    });
    setShowModal(true);
  };

  const handleLoyaltyUpdate = async () => {
    if (!selectedCustomer || !loyaltyPoints) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/customers/${selectedCustomer._id}/loyalty`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          points: parseInt(loyaltyPoints),
          action: loyaltyAction
        })
      });

      if (res.ok) {
        setShowLoyaltyModal(false);
        setLoyaltyPoints('');
        fetchCustomers();
        toast.success(loyaltyAction === 'add' ? 'Points added' : 'Points redeemed');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.msg || 'Could not update points');
      }
    } catch (err) {
      toast.error('Could not update points');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Deactivate customer?',
      message: 'This customer will be deactivated. You can reactivate them later if needed.',
      variant: 'danger',
      confirmText: 'Deactivate'
    });
    if (!ok) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/customers/${id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });
      toast.success('Customer deactivated');
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to deactivate customer');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      dateOfBirth: '',
      preferences: [],
      allergies: [],
      notes: ''
    });
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Manage customer records, loyalty points, and tier progression."
        action={
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => { resetForm(); setEditingCustomer(null); setShowModal(true); }}
          >
            Add Customer
          </Button>
        }
      />

      {/* Loyalty Program Info */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
          <Award size={16} className="text-neutral-500" />
          <h3 className="text-sm font-semibold text-neutral-800">Loyalty program tiers</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-5">
          {tierInfo.map((tier) => (
            <div key={tier.tier} className={`p-4 rounded-md border ${tierAccent(tier.tier)}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold capitalize text-sm text-neutral-900">{tier.tier}</p>
                <StatusBadge variant={tierVariant(tier.tier)} size="sm">
                  {tier.pointsMultiplier}×
                </StatusBadge>
              </div>
              <p className="text-xs text-neutral-600">
                Min spent <span className="font-semibold tabular-nums">{fmtMoney(tier.minSpent)}</span>
              </p>
              <p className="text-[11px] text-neutral-500 mt-1.5 leading-tight">
                {tier.benefits.join(' · ')}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 mb-6 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            type="text"
            placeholder="Search by name, phone, or email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search size={14} />}
          />
        </div>
        <div className="w-44">
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
          >
            <option value="all">All tiers</option>
            <option value="bronze">Bronze</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Tier</th>
              <th>Points</th>
              <th>Total Spent</th>
              <th>Orders</th>
              <th>Last Visit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={`s-${i}`} cols={8} />)}
            {!loading && customers.map(customer => (
              <tr key={customer._id}>
                <td className="font-medium">{customer.name}</td>
                <td className="text-neutral-600">{customer.phone}</td>
                <td>
                  <StatusBadge variant={tierVariant(customer.loyaltyTier)} dot>
                    {customer.loyaltyTier || 'bronze'}
                  </StatusBadge>
                </td>
                <td className="font-semibold tabular-nums">{customer.loyaltyPoints || 0}</td>
                <td className="tabular-nums">{fmtMoney(customer.totalSpent || 0, true)}</td>
                <td className="tabular-nums">{customer.totalOrders || 0}</td>
                <td className="text-sm text-neutral-600">
                  {customer.lastVisit
                    ? new Date(customer.lastVisit).toLocaleDateString()
                    : <span className="text-neutral-400">Never</span>}
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Coins size={13} />}
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowLoyaltyModal(true);
                      }}
                    >
                      Points
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => handleEdit(customer)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(customer._id)}>
                      Deactivate
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && customers.length === 0 && (
              <tr>
                <td colSpan="8">
                  <EmptyState
                    icon={Users}
                    title={searchTerm ? 'No customers match your search' : 'No customers yet'}
                    message={searchTerm
                      ? `Nothing matches "${searchTerm}". Try a different name or phone.`
                      : 'Add your first customer to start tracking visits and loyalty points.'}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-neutral-900">
                {editingCustomer ? 'Edit customer' : 'Add new customer'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); setEditingCustomer(null); }}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <Input
                  label="Name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
                <Input
                  label="Phone"
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
                <Input
                  label="Email (optional)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
                <Input
                  label="Date of birth (optional)"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                />
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Address (optional)</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    rows="2"
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="2"
                    placeholder="Allergies, preferences, etc."
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => { setShowModal(false); setEditingCustomer(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth>
                  {editingCustomer ? 'Update customer' : 'Add customer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loyalty Points Modal */}
      {showLoyaltyModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-sm">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
                <Coins size={18} className="text-warning-600" />
                Loyalty points
              </h2>
              <button
                type="button"
                onClick={() => { setShowLoyaltyModal(false); setLoyaltyPoints(''); }}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Customer summary */}
              <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-md">
                <p className="text-sm font-semibold text-neutral-900">{selectedCustomer.name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{selectedCustomer.phone}</p>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span className="text-neutral-600">
                    Current: <span className="font-semibold text-neutral-900 tabular-nums">{selectedCustomer.loyaltyPoints || 0}</span> pts
                  </span>
                  <StatusBadge variant={tierVariant(selectedCustomer.loyaltyTier)} size="sm">
                    {selectedCustomer.loyaltyTier || 'bronze'}
                  </StatusBadge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-700 block mb-1">Action</label>
                <select
                  value={loyaltyAction}
                  onChange={(e) => setLoyaltyAction(e.target.value)}
                  className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                >
                  <option value="add">Add points</option>
                  <option value="redeem">Redeem points (min 100)</option>
                </select>
              </div>

              <Input
                label="Points"
                type="number"
                value={loyaltyPoints}
                onChange={(e) => setLoyaltyPoints(e.target.value)}
                placeholder={loyaltyAction === 'redeem' ? 'Min 100 points' : 'Points to add'}
              />

              {loyaltyAction === 'redeem' && loyaltyPoints >= 100 && (
                <div className="p-2.5 bg-success-50 border border-success-200 rounded text-xs text-success-700">
                  Redemption value: <span className="font-semibold tabular-nums">{fmtMoney(loyaltyPoints)}</span> discount
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => { setShowLoyaltyModal(false); setLoyaltyPoints(''); }}
              >
                Cancel
              </Button>
              <Button
                variant={loyaltyAction === 'redeem' ? 'success' : 'primary'}
                fullWidth
                onClick={handleLoyaltyUpdate}
                disabled={!loyaltyPoints}
              >
                Update points
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Customers;
